/**
 * Edit one billing rule: what it checks, the numbers it needs, and whether a
 * break blocks approval or only warns. Opened for a new rule typed in plain
 * English (prefilled from the parse) and for an extracted rule.
 */
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import { Segmented } from "../settings/SettingRow";
import type { BillingRule, RuleKind, RuleParams } from "../types";
import { useHeld } from "../useHeld";
import {
  CLASSIFICATIONS,
  kindOf,
  PARAM_LABEL,
  paramsFor,
  RULE_KINDS,
  ruleIssue,
  SEVERITY_OPTIONS
} from "./ruleModel";

const ANY = "__any";

type Props = {
  rule: BillingRule | null;
  title: string;
  /** Shown when the model didn't answer and keywords picked the kind. */
  guessed?: boolean;
  saveLabel?: string;
  onSave: (rule: BillingRule) => void;
  onClose: () => void;
};

const Editor: FC<Omit<Props, "rule"> & { rule: BillingRule }> = ({
  rule,
  title,
  guessed,
  saveLabel = "Save rule",
  onSave,
  onClose
}) => {
  const [draft, setDraft] = useState<BillingRule>(rule);
  const issue = ruleIssue(draft);
  const setParam = (key: keyof RuleParams, value: string | number | null) =>
    setDraft((d) => ({
      ...d,
      params: { ...d.params, [key]: value === null ? undefined : value }
    }));
  const setKind = (kind: RuleKind) =>
    setDraft((d) => ({ ...d, kind, params: paramsFor(kind, d.params) }));
  return (
    <DialogContent className="sm:max-w-130" data-testid="rule-editor">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {guessed
            ? "Couldn't read this one with the assistant, so check the kind and numbers."
            : "Pre-bill checks released time against this rule."}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-2">
          <span className="flex flex-col gap-1">
            <Label htmlFor="rule-section" className="text-sm">
              Section
            </Label>
            <Input
              id="rule-section"
              placeholder="2.1"
              className="h-8 text-base"
              value={draft.section ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, section: e.currentTarget.value })
              }
            />
          </span>
          <span className="flex flex-col gap-1">
            <Label htmlFor="rule-label" className="text-sm">
              Rule
            </Label>
            <Input
              id="rule-label"
              className="h-8 text-base"
              value={draft.label}
              onChange={(e) =>
                setDraft({ ...draft, label: e.currentTarget.value })
              }
            />
          </span>
        </div>
        <span className="flex flex-col gap-1">
          <Label htmlFor="rule-kind" className="text-sm">
            Checks for
          </Label>
          <Select
            value={draft.kind}
            onValueChange={(v) => setKind(v as RuleKind)}
          >
            <SelectTrigger id="rule-kind" size="sm" className="w-55">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RULE_KINDS.map((k) => (
                <SelectItem key={k.id} value={k.id}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </span>
        {kindOf(draft.kind).needs.length || draft.kind === "rate_cap" ? (
          <div className="flex flex-wrap gap-2">
            {kindOf(draft.kind).needs.map((p) => (
              <span key={p} className="flex flex-col gap-1">
                <Label htmlFor={`rule-${p}`} className="text-sm">
                  {PARAM_LABEL[p]}
                </Label>
                <Input
                  id={`rule-${p}`}
                  inputMode="decimal"
                  className="h-8 w-30 text-base tabular-nums"
                  value={draft.params[p] ?? ""}
                  onChange={(e) => {
                    const n = Number(
                      e.currentTarget.value.replace(/[^0-9.]/g, "")
                    );
                    setParam(p, e.currentTarget.value ? n : null);
                  }}
                />
              </span>
            ))}
            {draft.kind === "rate_cap" ? (
              <span className="flex flex-col gap-1">
                <Label htmlFor="rule-classification" className="text-sm">
                  Applies to
                </Label>
                <Select
                  value={draft.params.classification ?? ANY}
                  onValueChange={(v) =>
                    setParam("classification", v === ANY ? null : v)
                  }
                >
                  <SelectTrigger
                    id="rule-classification"
                    size="sm"
                    className="w-40"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Everyone</SelectItem>
                    {CLASSIFICATIONS.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </span>
            ) : null}
            {draft.kind === "hours_threshold" ? (
              <>
                <span className="flex flex-col gap-1">
                  <Label htmlFor="rule-per" className="text-sm">
                    Counts
                  </Label>
                  <Select
                    value={draft.params.per ?? "matter"}
                    onValueChange={(v) => setParam("per", v)}
                  >
                    <SelectTrigger id="rule-per" size="sm" className="w-37.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entry">Each Entry</SelectItem>
                      <SelectItem value="matter">The Matter Total</SelectItem>
                    </SelectContent>
                  </Select>
                </span>
                <span className="flex flex-col gap-1">
                  <Label htmlFor="rule-activity" className="text-sm">
                    Work
                  </Label>
                  <Input
                    id="rule-activity"
                    placeholder="Any work"
                    className="h-8 w-35 text-base"
                    value={draft.params.activity ?? ""}
                    onChange={(e) =>
                      setParam("activity", e.currentTarget.value || null)
                    }
                  />
                </span>
                <span className="flex flex-col gap-1">
                  <Label htmlFor="rule-needs" className="text-sm">
                    Past it, needs
                  </Label>
                  <Input
                    id="rule-needs"
                    placeholder="Nothing, it's a limit"
                    className="h-8 w-42.5 text-base"
                    value={draft.params.needs ?? ""}
                    onChange={(e) =>
                      setParam("needs", e.currentTarget.value || null)
                    }
                  />
                </span>
              </>
            ) : null}
          </div>
        ) : null}
        <span className="flex flex-col gap-1">
          <span className="text-sm font-medium">When an entry breaks it</span>
          <Segmented
            label="When an entry breaks it"
            value={draft.severity}
            options={SEVERITY_OPTIONS}
            onChange={(severity) => setDraft({ ...draft, severity })}
          />
        </span>
        {draft.quote ? (
          <blockquote className="border-l border-line-3 pl-2.5 text-sm leading-4.5 text-fg-3">
            &ldquo;{draft.quote}&rdquo;
          </blockquote>
        ) : null}
      </div>
      <DialogFooter className="items-center">
        {issue ? (
          <span className="mr-auto text-sm text-warn">{issue}</span>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={Boolean(issue) || !draft.label.trim()}
          onClick={() =>
            onSave({
              ...draft,
              label: draft.label.trim(),
              section: draft.section?.trim() || undefined,
              params: paramsFor(draft.kind, draft.params)
            })
          }
        >
          {saveLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export const RuleEditor: FC<Props> = ({ rule, onClose, ...rest }) => {
  const shown = useHeld(rule);
  return (
    <Dialog open={Boolean(rule)} onOpenChange={(open) => !open && onClose()}>
      {shown ? (
        <Editor key={shown.id} rule={shown} onClose={onClose} {...rest} />
      ) : null}
    </Dialog>
  );
};
